import { useEffect, useState } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import useIdleTimeout from '../hooks/useIdleTimeout';

// ── Forced password change ────────────────────────────────────────────────
// Shown when an admin reset flagged the account. Deliberately not dismissable:
// no close button, and clicking the backdrop does nothing. The server refuses
// writes from a flagged account anyway, so this is the way out, not a nag.
function ForcePasswordChange({ onDone }) {
  const { user } = useAuth();
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [rule, setRule] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/auth/password-policy').then(r => setRule(r.data.description || '')).catch(() => {});
  }, []);

  const save = async () => {
    if (pw !== confirm) return toast.error('The two passwords do not match');
    setSaving(true);
    try {
      await api.patch(`/auth/users/${user.id}/password`, { password: pw });
      toast.success('Password updated');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not change the password');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-header"><h3>Choose a new password</h3></div>
        <div className="modal-body">
          <p className="text-sm text-muted" style={{ marginTop: 0 }}>
            Your password was reset by an administrator. Pick a new one to carry on.
          </p>
          {rule && <p className="form-hint" style={{ marginTop: 0 }}>{rule}</p>}
          <div className="form-group">
            <label className="form-label">New password<span className="required">*</span></label>
            <input className="form-input" type="password" value={pw} autoFocus
              onChange={e => setPw(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm new password<span className="required">*</span></label>
            <input className="form-input" type="password" value={confirm}
              onChange={e => setConfirm(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && pw && confirm) save(); }} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={save} disabled={saving || !pw || !confirm}>
            {saving ? 'Saving…' : 'Set password'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Idle timeout warning ──────────────────────────────────────────────────
function IdleWarning({ secondsLeft, onStay }) {
  return (
    <div className="modal-overlay" style={{ zIndex: 1050 }}>
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header"><h3>Still there?</h3></div>
        <div className="modal-body">
          <p style={{ marginTop: 0 }}>
            You'll be signed out in <strong>{secondsLeft}</strong> second{secondsLeft === 1 ? '' : 's'} because
            of inactivity. Anything you have typed but not submitted will be lost.
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onStay}>Stay signed in</button>
        </div>
      </div>
    </div>
  );
}

// Both gates in one place so Layout only mounts one component.
export default function SecurityGates() {
  const { user, idleMinutes, refreshMe, logout } = useAuth();

  const handleTimeout = async () => {
    try { await logout(); } catch (_) { /* sign out locally regardless */ }
    window.location.href = '/login?timeout=1';
  };
  const { secondsLeft, staySignedIn } = useIdleTimeout(idleMinutes, handleTimeout);

  if (user?.must_change_password) return <ForcePasswordChange onDone={refreshMe} />;
  if (secondsLeft != null) return <IdleWarning secondsLeft={secondsLeft} onStay={staySignedIn} />;
  return null;
}
